import React from 'react';
import { useState, useMemo, useCallback } from 'react';
import { Globe, Compass, MapPin, Buildings, CaretRight, X, WifiHigh } from '@phosphor-icons/react';
import { useSearchFilters } from '../../contexts/SearchContext';
import { useFacetsForAttribute } from '../../contexts/FacetsContext';
import { useDebounce } from '../../hooks/useDebounce';
import { getFilterModalCategoryColors } from '../../utils/filters/filter-attribute-category';
import { getRegionTagLabel, type RegionTagType } from '../../utils/facets/region-tag-labels';
import { FINLAND_COUNTRY_PLACE_ID } from '../../utils/geocoding/finland-place-ids';
import { FINLAND_ADMIN1_PLACE_IDS, getFinlandRegionChildren } from '../../utils/facets/finland-region-hierarchy';
import { Msg } from '../../i18n-review/client/Msg';
import { FilterSwitch } from '../../design-system/FilterSwitch';
import { FinlandGlobeFlagThumb } from './FinlandGlobeFlagThumb';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setFinlandFilterEnabled } from '../../redux/features/finlandFilterSlice';

const ATTRIBUTE = 'regionTags';

interface Tile {
  value: string;
  label: string;
  count: number;
  /** How many of `count`'s jobs are also remoteness=Remote. Undefined until the companion facet
   * query behind it has run (see mergeRegionTagsRemoteCounts / pages/index/+data.ts). */
  remoteCount?: number;
  type: RegionTagType;
  /** Whether clicking the tile body (not its checkbox) drills into a deeper level. */
  drillable: boolean;
}

type PickerLevel =
  | { kind: 'root' }
  | { kind: 'regionCities'; admin1PlaceId: string; admin1Label: string };

/**
 * TEC-482: replaces the old flat, ~930-item merged "Finland" bucket in CustomRefinementList's
 * regionTags branch with a real drill-down: pick a broad level (Finland, a recruiting region,
 * a continent) or step into a maakunta to see its cities. Multi-select stays exactly as before,
 * every tile's checkbox toggles that placeId in/out of the same `regionTags` filter array
 * CustomRefinementList already manages; drilling in is a separate action from selecting, so a
 * user can pick a broad level, a specific one, or both at once. The root level shows Finland's
 * 18 maakunta inline (not behind an extra click, this site is Finland-targeted) alongside the
 * pinned whole-country Finland tile; only stepping from a maakunta into its individual cities is
 * still a distinct drilled-into level, cheap because it's just Finland's fixed hierarchy but too
 * many tiles to also show inline at the root. See docs/knowledge-graph/01-job-search-discovery.md
 * and the TEC-482 plan for context.
 */
export const LocationHierarchyPicker: React.FC<{ intl: any; formatMessage: any; searchQuery?: string }> = ({
  intl,
  formatMessage,
  searchQuery,
}) => {
  const appLocale: 'en' | 'fi' | 'sv' = intl.locale === 'fi' || intl.locale === 'sv' ? intl.locale : 'en';
  const { filters, setFilters } = useSearchFilters();
  const dispatch = useAppDispatch();
  const isFinlandEnabled = useAppSelector((state) => state.finlandFilter?.enabled ?? true);
  const current = ((filters as Record<string, string[]>)?.[ATTRIBUTE] || []) as string[];
  const excludeKey = `exclude_${ATTRIBUTE}`;
  const excluded = ((filters as Record<string, string[]>)?.[excludeKey] || []) as string[];
  const currentSet = useMemo(() => new Set(current.map((v) => v.toLowerCase())), [current]);
  const excludedSet = useMemo(() => new Set(excluded.map((v) => v.toLowerCase())), [excluded]);

  const { items: facetItems } = useFacetsForAttribute(ATTRIBUTE);
  const countByValue = useMemo(() => {
    const map = new Map<string, number>();
    facetItems.forEach((item) => map.set(item.value, item.count));
    return map;
  }, [facetItems]);
  // Only populated once pages/index/+data.ts's companion remoteness-scoped facet query has run
  // (mergeRegionTagsRemoteCounts); a value simply absent from the map (not 0) means "not fetched
  // yet", so tiles fall back to omitting the remote badge rather than showing a false zero.
  const remoteCountByValue = useMemo(() => {
    const map = new Map<string, number>();
    facetItems.forEach((item) => {
      if (typeof item.remoteCount === 'number') map.set(item.value, item.remoteCount);
    });
    return map;
  }, [facetItems]);

  const [level, setLevel] = useState<PickerLevel>({ kind: 'root' });
  const [showBeyondFinland, setShowBeyondFinland] = useState(false);
  const debouncedSearchQuery = useDebounce(searchQuery || '', 200);
  const isSearching = debouncedSearchQuery.trim().length > 0;

  const colors = getFilterModalCategoryColors(ATTRIBUTE);
  const beyondFinlandToggleLabelId = 'location-picker-beyond-finland-toggle';

  const toggleInclude = useCallback(
    (value: string) => {
      const isSelected = currentSet.has(value.toLowerCase());
      const base = filters && typeof filters === 'object' ? (filters as Record<string, string[]>) : {};
      const nextFilters: Record<string, string[]> = { ...base };
      if (isSelected) {
        const remaining = current.filter((v) => v.toLowerCase() !== value.toLowerCase());
        if (remaining.length > 0) nextFilters[ATTRIBUTE] = remaining;
        else delete nextFilters[ATTRIBUTE];
      } else {
        nextFilters[ATTRIBUTE] = [...current, value];
        // Picking a real location tile always supersedes "World" (which works by turning this
        // flag off rather than by writing a regionTags value of its own, see selectWorld below).
        if (!isFinlandEnabled) dispatch(setFinlandFilterEnabled(true));
      }
      setFilters(Object.keys(nextFilters).length > 0 ? nextFilters : {});
    },
    [current, currentSet, filters, setFilters, isFinlandEnabled, dispatch]
  );

  const toggleExclude = useCallback(
    (value: string, e: React.MouseEvent) => {
      e.stopPropagation();
      const isExcluded = excludedSet.has(value.toLowerCase());
      const base = filters && typeof filters === 'object' ? (filters as Record<string, string[]>) : {};
      const nextFilters: Record<string, string[]> = { ...base };
      if (currentSet.has(value.toLowerCase())) {
        const remaining = current.filter((v) => v.toLowerCase() !== value.toLowerCase());
        if (remaining.length > 0) nextFilters[ATTRIBUTE] = remaining;
        else delete nextFilters[ATTRIBUTE];
      }
      if (isExcluded) {
        const remaining = excluded.filter((v) => v.toLowerCase() !== value.toLowerCase());
        if (remaining.length > 0) nextFilters[excludeKey] = remaining;
        else delete nextFilters[excludeKey];
      } else {
        nextFilters[excludeKey] = [...excluded, value];
      }
      setFilters(Object.keys(nextFilters).length > 0 ? nextFilters : {});
    },
    [current, currentSet, excluded, excludedSet, excludeKey, filters, setFilters]
  );

  // "World": not a regionTags value at all (there's no single gazetteer placeId meaning "every
  // country"), it's the absence of any location narrowing. Selecting it clears any regionTags
  // selection and turns off the site's Finland-baseline default (`isFinlandEnabled`, the same
  // flag FinlandFilterToggle drives in the old location system) so every active job, anywhere,
  // matches. Picking any other tile (see toggleInclude above) turns the baseline flag back on.
  const worldLabel = formatMessage({
    id: 'search.refinements.regionTags.worldQuickChip',
    defaultMessage: 'World',
    description: 'Quick-toggle chip that clears all location narrowing so every active job everywhere matches, regardless of location',
  });
  const isWorldSelected = current.length === 0 && !isFinlandEnabled;
  const selectWorld = useCallback(() => {
    const base = filters && typeof filters === 'object' ? (filters as Record<string, string[]>) : {};
    const nextFilters: Record<string, string[]> = { ...base };
    delete nextFilters[ATTRIBUTE];
    delete nextFilters[excludeKey];
    setFilters(Object.keys(nextFilters).length > 0 ? nextFilters : {});
    dispatch(setFinlandFilterEnabled(false));
  }, [filters, excludeKey, setFilters, dispatch]);
  const deselectWorld = useCallback(() => {
    dispatch(setFinlandFilterEnabled(true));
  }, [dispatch]);

  // Root-level tiles: pinned Finland, then recruiting regions, then continents. Countries other
  // than Finland fall back to a flat searchable list below, 252 possible values is too many for
  // tiles, and almost all return a zero facet count in practice (only real postings' countries
  // ever appear here).
  const finlandTile: Tile = {
    value: FINLAND_COUNTRY_PLACE_ID,
    label: formatMessage({
      id: 'search.refinements.regionTags.finlandQuickChip',
      defaultMessage: 'Finland',
      description: 'Quick-toggle chip that filters the region facet to Finland-only locations',
    }),
    count: countByValue.get(FINLAND_COUNTRY_PLACE_ID) ?? 0,
    remoteCount: remoteCountByValue.get(FINLAND_COUNTRY_PLACE_ID),
    type: 'country',
    // No longer a distinct drill target: its maakunta already render inline below it at root.
    drillable: false,
  };

  const recruitingRegionTiles: Tile[] = useMemo(
    () =>
      facetItems
        .filter((item) => item.regionTagType === 'recruitingRegion')
        .map((item) => ({ value: item.value, label: item.label, count: item.count, remoteCount: item.remoteCount, type: 'recruitingRegion' as const, drillable: false })),
    [facetItems]
  );

  const continentTiles: Tile[] = useMemo(
    () =>
      facetItems
        .filter((item) => item.regionTagType === 'continent')
        .map((item) => ({ value: item.value, label: item.label, count: item.count, remoteCount: item.remoteCount, type: 'continent' as const, drillable: false })),
    [facetItems]
  );

  const otherCountryTiles: Tile[] = useMemo(
    () =>
      facetItems
        .filter((item) => item.regionTagType === 'country' && item.value !== FINLAND_COUNTRY_PLACE_ID)
        .map((item) => ({ value: item.value, label: item.label, count: item.count, remoteCount: item.remoteCount, type: 'country' as const, drillable: false })),
    [facetItems]
  );

  // This board is Finland-targeted: the root level shows Finland alone by default, with
  // recruiting regions/continents/other countries revealed behind an explicit toggle rather than
  // presented as equal-weight tiles alongside it. Auto-reveal if the current filters already
  // include a non-Finland value (so navigating back here doesn't visually hide an active
  // selection) or while searching (search should match across everything, not just Finland).
  const nonFinlandValues = useMemo(
    () =>
      new Set(
        [...recruitingRegionTiles, ...continentTiles, ...otherCountryTiles].map((t) => t.value.toLowerCase())
      ),
    [recruitingRegionTiles, continentTiles, otherCountryTiles]
  );
  const hasNonFinlandSelection = useMemo(
    () =>
      current.some((v) => nonFinlandValues.has(v.toLowerCase())) ||
      excluded.some((v) => nonFinlandValues.has(v.toLowerCase())),
    [current, excluded, nonFinlandValues]
  );
  const shouldShowBeyondFinland = showBeyondFinland || hasNonFinlandSelection || isSearching;
  // The toggle switches the root view between "Finland's own regions" and "the rest of the
  // world" rather than accumulating both: once deliberately viewing beyond Finland (manual
  // toggle, or an existing non-Finland selection), the 18 maakunta step back out of the way so
  // the recruiting-region/continent tiles read as the default again, like before maakunta were
  // inline. Search is the one exception - it should still match Finland's own regions too, not
  // just the global ones, so both sets stay visible (each filtered by applySearch) while typing.
  const showFinlandRegionsInline = !shouldShowBeyondFinland || isSearching;

  // Level 1: the 18 maakunta, joined against facet counts (0 for a region with no matching jobs
  // under the current filters, still shown, same "never silently drop a value" posture the old
  // flat list had).
  const admin1Tiles: Tile[] = useMemo(
    () =>
      FINLAND_ADMIN1_PLACE_IDS.map((placeId) => ({
        value: placeId,
        label: getRegionTagLabel(placeId, appLocale),
        count: countByValue.get(placeId) ?? 0,
        remoteCount: remoteCountByValue.get(placeId),
        type: 'admin1' as const,
        drillable: true,
      })),
    [countByValue, remoteCountByValue, appLocale]
  );

  // Level 2: cities within the drilled-into region.
  const cityTiles: Tile[] = useMemo(() => {
    if (level.kind !== 'regionCities') return [];
    return getFinlandRegionChildren(level.admin1PlaceId).map((placeId) => ({
      value: placeId,
      label: getRegionTagLabel(placeId, appLocale),
      count: countByValue.get(placeId) ?? 0,
      remoteCount: remoteCountByValue.get(placeId),
      type: 'city' as const,
      drillable: false,
    }));
  }, [level, countByValue, remoteCountByValue, appLocale]);

  const applySearch = useCallback(
    (tiles: Tile[]) => {
      if (!isSearching) return tiles;
      const q = debouncedSearchQuery.toLowerCase().trim();
      return tiles.filter((t) => currentSet.has(t.value.toLowerCase()) || t.label.toLowerCase().includes(q));
    },
    [isSearching, debouncedSearchQuery, currentSet]
  );

  const renderTile = (tile: Tile) => {
    const isIncluded = currentSet.has(tile.value.toLowerCase());
    const isExcluded = excludedSet.has(tile.value.toLowerCase());
    const Icon = TILE_ICON[tile.type];
    const remoteLabel = tile.remoteCount
      ? formatMessage(
          {
            id: 'search.refinements.regionTags.remoteCountLabel',
            defaultMessage: '{value} remote',
            description: 'Badge on a location tile in the hierarchical location filter picker, showing how many of that location\'s jobs are remote',
          },
          { value: String(tile.remoteCount) }
        )
      : null;
    return (
      <div
        key={tile.value}
        className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 transition-colors ${
          isIncluded
            ? `${colors.bg} ${colors.text} ${colors.border}`
            : isExcluded
            ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700'
            : 'border-[#DDD5C8] dark:border-[#4A3520] text-[#3D3529] dark:text-[#C4A882] hover:bg-[#F2EADF] dark:hover:bg-[#342415]'
        }`}
      >
        <button
          type="button"
          onClick={() => toggleInclude(tile.value)}
          className="flex-1 flex items-center gap-2 min-w-0 text-left cursor-pointer"
        >
          <Icon className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm truncate">{tile.label}</span>
          {tile.count > 0 && (
            <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${colors.badge}`}>{tile.count}</span>
          )}
          {!!tile.remoteCount && remoteLabel && (
            <span
              className="flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 bg-[#E4EEE7] dark:bg-[#1E3327] text-[#3B5E45] dark:text-[#98CFA8]"
              title={remoteLabel}
            >
              <WifiHigh className="w-3 h-3" weight="bold" aria-hidden="true" />
              <span aria-hidden="true">{tile.remoteCount}</span>
              <span className="sr-only">{remoteLabel}</span>
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={(e) => toggleExclude(tile.value, e)}
          aria-label={formatMessage(
            isExcluded
              ? { id: 'search.refinements.removeExclude', defaultMessage: 'Remove exclusion of {value}', description: 'Accessibility label for the button that removes a negative (excluded) filter value' }
              : { id: 'search.refinements.exclude', defaultMessage: 'Exclude {value}', description: 'Accessibility label for the button that excludes a filter value from search results' },
            { value: tile.label }
          )}
          aria-pressed={isExcluded}
          className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-xs transition-colors ${
            isExcluded
              ? 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400'
              : 'text-[#7A6E5F] dark:text-[#8A7060] hover:text-red-600 dark:hover:text-red-400'
          }`}
        >
          <X className="w-3.5 h-3.5" weight="bold" />
        </button>
        {tile.drillable && (
          <button
            type="button"
            onClick={() => setLevel({ kind: 'regionCities', admin1PlaceId: tile.value, admin1Label: tile.label })}
            aria-label={formatMessage({
              id: 'search.refinements.regionTags.drillIn',
              defaultMessage: 'Browse {value}',
              description: 'Accessibility label for the button that drills into a location tile\'s sub-regions',
            }, { value: tile.label })}
            className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-[#7A6E5F] dark:text-[#8A7060] hover:bg-[#F2EADF] dark:hover:bg-[#342415]"
          >
            <CaretRight className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  };

  const breadcrumbSegments: { key: string; label: React.ReactNode; onClick?: () => void }[] = [
    {
      key: 'root',
      label: <Msg id="search.refinements.regionTags.breadcrumb.root" defaultMessage="All Locations" description="Root breadcrumb label in the hierarchical location filter picker" />,
      onClick: level.kind !== 'root' ? () => setLevel({ kind: 'root' }) : undefined,
    },
  ];
  if (level.kind === 'regionCities') {
    breadcrumbSegments.push({ key: 'admin1', label: level.admin1Label });
  }

  return (
    <div className="space-y-3">
      {breadcrumbSegments.length > 1 && (
        <div className="flex items-center flex-wrap gap-1 text-xs text-[#7A6E5F] dark:text-[#8A7060]">
          {breadcrumbSegments.map((seg, i) => (
            <React.Fragment key={seg.key}>
              {i > 0 && <CaretRight className="w-3 h-3 flex-shrink-0" />}
              {seg.onClick ? (
                <button type="button" onClick={seg.onClick} className="hover:underline hover:text-[#3B5E45] dark:hover:text-[#98CFA8] cursor-pointer">
                  {seg.label}
                </button>
              ) : (
                <span className="font-medium text-[#3D3529] dark:text-[#C4A882]">{seg.label}</span>
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      <div key={level.kind === 'regionCities' ? level.admin1PlaceId : level.kind} className="grid grid-cols-1 sm:grid-cols-2 gap-2 animate-[filterSectionIn_200ms_ease-out]">
        {level.kind === 'root' && (!isSearching || worldLabel.toLowerCase().includes(debouncedSearchQuery.toLowerCase().trim())) && (
          <button
            type="button"
            onClick={isWorldSelected ? deselectWorld : selectWorld}
            className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 transition-colors text-left cursor-pointer ${
              isWorldSelected
                ? `${colors.bg} ${colors.text} ${colors.border}`
                : 'border-[#DDD5C8] dark:border-[#4A3520] text-[#3D3529] dark:text-[#C4A882] hover:bg-[#F2EADF] dark:hover:bg-[#342415]'
            }`}
          >
            <Globe className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm truncate">{worldLabel}</span>
          </button>
        )}
        {level.kind === 'root' && (
          <>
            {renderTile(finlandTile)}
            {showFinlandRegionsInline && applySearch(admin1Tiles).map(renderTile)}
            {shouldShowBeyondFinland && applySearch(recruitingRegionTiles).map(renderTile)}
            {shouldShowBeyondFinland && applySearch(continentTiles).map(renderTile)}
          </>
        )}
        {level.kind === 'regionCities' && applySearch(cityTiles).map(renderTile)}
      </div>

      {level.kind === 'root' && (
        <div className="flex items-center justify-between gap-4 py-1 px-1">
          <span id={beyondFinlandToggleLabelId} className="text-sm text-[#7A6E5F] dark:text-[#8A7060]">
            <Msg
              id="search.refinements.regionTags.showBeyondFinland"
              defaultMessage="Show locations outside Finland"
              description="Toggle label that reveals recruiting-region/continent/country tiles in the Finland-first location filter picker"
            />
          </span>
          <FilterSwitch
            checked={shouldShowBeyondFinland}
            onCheckedChange={setShowBeyondFinland}
            disabled={hasNonFinlandSelection || isSearching}
            aria-labelledby={beyondFinlandToggleLabelId}
            thumbContent={<FinlandGlobeFlagThumb finlandActive={!shouldShowBeyondFinland} />}
          />
        </div>
      )}

      {level.kind === 'root' && shouldShowBeyondFinland && applySearch(otherCountryTiles).length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-[#7A6E5F] dark:text-[#8A7060] px-1">
            <Msg id="search.refinements.regionTags.group.country" defaultMessage="Countries" description="Location filter group heading for countries" />
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">{applySearch(otherCountryTiles).map(renderTile)}</div>
        </div>
      )}
    </div>
  );
};

LocationHierarchyPicker.displayName = 'LocationHierarchyPicker';

const TILE_ICON: Record<RegionTagType, React.ComponentType<{ className?: string }>> = {
  recruitingRegion: Compass,
  continent: Globe,
  country: Globe,
  admin1: MapPin,
  city: Buildings,
};
